import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCandidateInfo } from '../constants/candidatesData';
import '../styles/ResultsPage.css';

const ResultsPage = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFaculty, setSelectedFaculty] = useState('engineering');
  const [selectedMajor, setSelectedMajor] = useState(null);

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/public/results`);
      
      if (!response.ok) {
        throw new Error('خطا در دریافت نتایج');
      }
      
      const data = await response.json();
      setResults(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const getMajorPersianName = (majorCode) => {
    const majorMap = {
      'computer': 'مهندسی کامپیوتر',
      'chemical': 'مهندسی شیمی',
      'architecture': 'مهندسی معماری',
      'electrical': 'مهندسی برق',
      'civil': 'مهندسی عمران',
      'mechanical': 'مهندسی مکانیک',
      'industrial': 'مهندسی صنایع',
      'surveying': 'مهندسی نقشه برداری',
      'biomedical': 'برق-پزشکی',
      'environment': 'مهندسی محیط زیست',
      'management': 'مدیریت',
      'psychology': 'روانشناسی',
      'accounting': 'حسابداری',
      'law': 'حقوق',
      'physical': 'تربیت بدنی و علوم ورزشی'
    };
    return majorMap[majorCode] || majorCode;
  };

  const getFacultyPersianName = (facultyCode) => {
    const facultyMap = {
      'engineering': 'دانشکده فنی و مهندسی',
      'humanities': 'دانشکده علوم انسانی',
      'sports': 'دانشکده علوم ورزشی'
    };
    return facultyMap[facultyCode] || facultyCode;
  };

  const getMajorIcon = (majorCode) => {
    const iconMap = {
      'computer': 'laptop-code',
      'chemical': 'flask',
      'architecture': 'drafting-compass',
      'electrical': 'bolt',
      'civil': 'building',
      'mechanical': 'cogs',
      'industrial': 'industry',
      'environment': 'leaf',
      'management': 'briefcase',
      'psychology': 'brain',
      'accounting': 'calculator',
      'law': 'balance-scale',
      'physical': 'running'
    };
    return iconMap[majorCode] || 'graduation-cap';
  };

  const getMajorColor = (majorCode) => {
    const colorMap = {
      'computer': '#3B82F6',
      'chemical': '#F59E0B',
      'architecture': '#10B981',
      'electrical': '#EF4444',
      'civil': '#8B5CF6',
      'mechanical': '#EC4899',
      'industrial': '#06B6D4',
      'environment': '#F97316',
      'management': '#3B82F6',
      'psychology': '#F59E0B',
      'accounting': '#10B981',
      'law': '#EF4444',
      'physical': '#8B5CF6'
    };
    return colorMap[majorCode] || '#6B7280';
  };

  const groupResultsByFaculty = () => {
    if (!results?.results_by_major) return {};
    
    // Fix: Move candidate 13 (شقایق صادقی) from biomedical to chemical
    const fixedResults = JSON.parse(JSON.stringify(results.results_by_major));
    
    // Find and remove candidate 13 from biomedical
    if (fixedResults.biomedical?.candidates) {
      const candidate13Index = fixedResults.biomedical.candidates.findIndex(c => c.candidate_id === 13);
      if (candidate13Index !== -1) {
        const candidate13 = fixedResults.biomedical.candidates.splice(candidate13Index, 1)[0];
        
        // Add candidate 13 to chemical
        if (fixedResults.chemical?.candidates) {
          fixedResults.chemical.candidates.push(candidate13);
          // Re-sort and re-rank chemical candidates
          fixedResults.chemical.candidates.sort((a, b) => b.votes - a.votes);
          fixedResults.chemical.candidates.forEach((c, idx) => c.rank = idx + 1);
        }
        
        // Re-rank biomedical candidates
        fixedResults.biomedical.candidates.forEach((c, idx) => c.rank = idx + 1);
      }
    }
    
    const grouped = {};
    Object.entries(fixedResults).forEach(([majorCode, data]) => {
      const faculty = data.faculty;
      if (!grouped[faculty]) {
        grouped[faculty] = [];
      }
      grouped[faculty].push({
        majorCode,
        majorName: getMajorPersianName(majorCode),
        majorIcon: getMajorIcon(majorCode),
        majorColor: getMajorColor(majorCode),
        candidates: data.candidates
      });
    });
    return grouped;
  };

  const getActiveFacultyMajors = () => {
    const grouped = groupResultsByFaculty();
    return grouped[selectedFaculty] || [];
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return { icon: '🥇', class: 'rank-1' };
    if (rank === 2) return { icon: '🥈', class: 'rank-2' };
    if (rank === 3) return { icon: '🥉', class: 'rank-3' };
    return { icon: rank, class: 'rank-other' };
  };

  const handleFacultyChange = (faculty) => {
    setSelectedFaculty(faculty);
    setSelectedMajor(null);
  };

  const handleMajorClick = (majorCode) => {
    setSelectedMajor(majorCode);
  };

  const handleBackToMajors = () => {
    setSelectedMajor(null);
  };

  if (loading) {
    return (
      <div className="results-page-wrapper">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>در حال بارگذاری نتایج انتخابات...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="results-page-wrapper">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>خطا در بارگذاری نتایج</h3>
          <p>{error}</p>
          <button onClick={fetchResults} className="retry-btn">تلاش مجدد</button>
        </div>
      </div>
    );
  }

  const hasResults = Object.keys(results?.results_by_major || {}).length > 0;
  const activeFacultyMajors = getActiveFacultyMajors();
  const selectedMajorData = selectedMajor ? activeFacultyMajors.find(m => m.majorCode === selectedMajor) : null;

  return (
    <div className="results-page-wrapper" data-testid="results-page">
      {/* Header */}
      <header className="results-header">
        <div className="header-content">
          <button onClick={() => navigate('/')} className="back-home-btn" data-testid="back-home-btn">
            <i className="fas fa-home"></i>
            بازگشت به صفحه اصلی
          </button>
          <div className="header-title">
            <h1>نتایج انتخابات انجمن‌های علمی</h1>
            <p className="header-subtitle">دانشگاه شمال آمل - سال تحصیلی ۱۴۰۴</p>
          </div>
          <div className="total-votes-badge">
            <span className="votes-icon">🗳️</span>
            <div>
              <div className="votes-label">مجموع آراء</div>
              <div className="votes-number">{results?.total_votes || 0}</div>
            </div>
          </div>
        </div>
      </header>

      {/* Faculty Tabs */}
      <div className="faculty-tabs-section">
        <div className="faculty-tabs">
          <button 
            className={`faculty-tab ${selectedFaculty === 'engineering' ? 'active' : ''}`}
            onClick={() => handleFacultyChange('engineering')}
            data-testid="faculty-tab-engineering"
          >
            <i className="fas fa-cogs"></i>
            <span>دانشکده فنی و مهندسی</span>
          </button>
          <button 
            className={`faculty-tab ${selectedFaculty === 'humanities' ? 'active' : ''}`}
            onClick={() => handleFacultyChange('humanities')}
            data-testid="faculty-tab-humanities"
          >
            <i className="fas fa-book"></i>
            <span>دانشکده علوم انسانی</span>
          </button>
          <button 
            className={`faculty-tab ${selectedFaculty === 'sports' ? 'active' : ''}`}
            onClick={() => handleFacultyChange('sports')}
            data-testid="faculty-tab-sports"
          >
            <i className="fas fa-running"></i>
            <span>دانشکده علوم ورزشی</span>
          </button>
        </div>
      </div>

      {/* Main Results */}
      <main className="results-main">
        {!hasResults ? (
          <div className="no-results-card">
            <div className="no-results-icon">📊</div>
            <h2>نتایج هنوز منتشر نشده است</h2>
            <p>پس از اتمام زمان رأی‌گیری و شمارش آراء، نتایج در این صفحه نمایش داده خواهد شد.</p>
          </div>
        ) : (
          <>
            {/* Major Selection View */}
            {!selectedMajor && (
              <div className="majors-grid" data-testid="majors-grid">
                {activeFacultyMajors.length === 0 ? (
                  <div className="no-results-card">
                    <div className="no-results-icon">🎓</div>
                    <h2>نتایجی برای این دانشکده وجود ندارد</h2>
                    <p>در حال حاضر نتایج رأی‌گیری برای این دانشکده ثبت نشده است.</p>
                  </div>
                ) : (
                  activeFacultyMajors.map(major => (
                    <div 
                      key={major.majorCode} 
                      className="major-result-card"
                      onClick={() => handleMajorClick(major.majorCode)}
                      style={{ '--major-color': major.majorColor }}
                      data-testid={`major-card-${major.majorCode}`}
                    >
                      <div className="major-result-icon">
                        <i className={`fas fa-${major.majorIcon}`}></i>
                      </div>
                      <h3 className="major-result-name">{major.majorName}</h3>
                      <div className="major-result-stats">
                        <div className="stat-item">
                          <i className="fas fa-users"></i>
                          <span>{major.candidates.length} نامزد</span>
                        </div>
                        <div className="stat-item">
                          <i className="fas fa-vote-yea"></i>
                          <span>{major.candidates.reduce((sum, c) => sum + c.votes, 0)} رأی</span>
                        </div>
                      </div>
                      <div className="top-candidate-preview">
                        {major.candidates[0] && (
                          <>
                            <span className="winner-badge">🥇 برنده</span>
                            <p className="winner-name">{getCandidateInfo(major.candidates[0].candidate_id).name}</p>
                            <p className="winner-votes">{major.candidates[0].votes} رأی</p>
                          </>
                        )}
                      </div>
                      <button className="view-details-btn">
                        مشاهده نتایج کامل
                        <i className="fas fa-arrow-left"></i>
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Major Detail View */}
            {selectedMajor && selectedMajorData && (
              <div className="major-detail-view" data-testid="major-detail-view">
                <div className="detail-header">
                  <button 
                    className="back-to-majors-btn"
                    onClick={handleBackToMajors}
                    data-testid="back-to-majors-btn"
                  >
                    <i className="fas fa-arrow-right"></i>
                    بازگشت به لیست رشته‌ها
                  </button>
                  <div className="detail-title">
                    <div 
                      className="detail-icon"
                      style={{ backgroundColor: selectedMajorData.majorColor }}
                    >
                      <i className={`fas fa-${selectedMajorData.majorIcon}`}></i>
                    </div>
                    <div>
                      <h2>{selectedMajorData.majorName}</h2>
                      <p className="detail-subtitle">{getFacultyPersianName(selectedFaculty)}</p>
                    </div>
                  </div>
                </div>

                <div className="candidates-results-list">
                  {selectedMajorData.candidates.map(candidate => {
                    const candidateInfo = getCandidateInfo(candidate.candidate_id);
                    const rankInfo = getRankBadge(candidate.rank);
                    
                    return (
                      <div 
                        key={candidate.candidate_id} 
                        className={`candidate-result-row ${rankInfo.class}`}
                        data-testid={`candidate-${candidate.candidate_id}`}
                      >
                        <div className="rank-badge">
                          <span className="rank-icon">{rankInfo.icon}</span>
                          <span className="rank-text">رتبه {candidate.rank}</span>
                        </div>
                        
                        <div className="candidate-photo">
                          {candidateInfo.photo ? (
                            <img src={candidateInfo.photo} alt={candidateInfo.name} />
                          ) : (
                            <div className={`photo-placeholder ${candidateInfo.gender}`}>
                              {candidateInfo.gender === 'female' ? '👩‍🎓' : '👨‍🎓'}
                            </div>
                          )}
                        </div>
                        
                        <div className="candidate-info">
                          <h4 className="candidate-name">{candidateInfo.name}</h4>
                          <p className="candidate-major">{candidateInfo.major}</p>
                        </div>
                        
                        <div className="vote-result">
                          <div className="vote-count">
                            <span className="vote-number">{candidate.votes}</span>
                            <span className="vote-label">رأی</span>
                          </div>
                          <div className="vote-percentage">
                            {((candidate.votes / selectedMajorData.candidates.reduce((sum, c) => sum + c.votes, 0)) * 100).toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="results-footer">
        <div className="footer-content">
          <div className="footer-main">
            <p className="copyright">© ۱۴۰۴ انجمن‌های علمی دانشگاه شمال آمل - سامانه رسمی رای‌گیری الکترونیک</p>
          </div>
          <div className="footer-credits">
            <img 
              src="https://customer-assets.emergentagent.com/job_election-results-2/artifacts/t453fnm4_Gemini_Generated_Image_zg3hvuzg3hvuzg3h-removebg-preview-removebg-preview.png" 
              alt="ARMAN" 
              className="footer-logo"
            />
            <span className="credits-text">توسعه: تیم نرم‌افزاری ارمان</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ResultsPage;